import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "./AuthContext";
import api from "./api";

const STORAGE_KEY = "venturaz_active_property";

/**
 * PropertyContext — powers the CRM multi-vertical switcher.
 *
 * Fetches `/api/me/properties` (returns the property catalogue the user
 * is allowed to see). Persists the active choice in localStorage so a
 * refresh keeps the same context.
 *
 * Sidebar sections and dashboards read `activeProperty` and filter
 * themselves. Super Admin sees every property; a debt-team user sees
 * only VenturaZ MONEY, etc.
 */
const PropertyContext = createContext({
  properties: [],
  activeProperty: null,
  setActiveProperty: () => {},
  loading: false,
});

export function PropertyProvider({ children }) {
  const { user } = useAuth();
  const [properties, setProperties] = useState([]);
  const [activeProperty, setActive] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!user) {
      setProperties([]);
      setActive(null);
      return;
    }
    setLoading(true);
    try {
      const r = await api.get("/me/properties");
      const items = r.data?.items || [];
      setProperties(items);
      // Restore stored choice if it's still accessible; otherwise fall back to first.
      let stored = null;
      try { stored = localStorage.getItem(STORAGE_KEY); } catch (_e) { /* ignore */ }
      const still_valid = items.find((p) => p.slug === stored);
      setActive(still_valid || items[0] || null);
    } catch (_e) {
      setProperties([]);
      setActive(null);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const setActiveProperty = useCallback((slug) => {
    const next = properties.find((p) => p.slug === slug);
    if (!next) return;
    setActive(next);
    try { localStorage.setItem(STORAGE_KEY, slug); } catch (_e) { /* ignore */ }
  }, [properties]);

  const value = useMemo(() => ({
    properties,
    activeProperty,
    setActiveProperty,
    loading,
  }), [properties, activeProperty, setActiveProperty, loading]);

  return <PropertyContext.Provider value={value}>{children}</PropertyContext.Provider>;
}

export function useProperty() {
  return useContext(PropertyContext);
}
